import {Suspense, useCallback, useEffect, useRef, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {PencilIcon} from '@primer/octicons-react'
import {Button, IconButton, PageHeader, useResponsiveValue} from '@primer/react'
import {useHeaderPageData} from '../page-data/loaders/use-header-page-data'
import {PullRequestBanners} from './PullRequestBanners'
import {FilesChangedFeedback} from './FilesChangedFeedback'
import {PullRequestHeaderSummary} from './PullRequestHeaderSummary'
import {PullRequestEditTitleForm} from './PullRequestEditTitleForm'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import {StackState} from './StackState'
import type {AliveChannels, HeaderPageData} from '../page-data/payloads/header'
import {PullRequestHeaderNavigation} from './PullRequestHeaderNavigation'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useHeaderLiveUpdates} from '../hooks/use-header-live-updates'
import {PullRequestCodeButton} from './PullRequestCodeButton'
import {DiffStat} from './DiffStat'
import {clsx} from 'clsx'
import styles from './PullRequestHeader.module.css'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {isLoggedIn} from '@github-ui/client-env'
import {MergeMethodContextProvider, MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {isPullRequestOpenState} from '../utils/pull-request-state'
import {StickyPullRequestHeader, type StickyPullRequestHeaderProps} from './StickyPullRequestHeader'
import {setTitle} from '@github-ui/document-metadata'
import {PullRequestsTopBanners} from './banners/PullRequestsTopBanners'
import {DockedPanel, useIsDockedPanelOpen, useDockedPanelActions} from '../contexts/DockedPanelContext'
import {MergeStatusPanel} from './toolbar/MergeStatusPanel'
import {useMergeStatusButtonData} from '../hooks/use-merge-status-button-data'
import type {PullRequestActiveRoute} from '../hooks/use-active-route'
import {getRouteConfig} from '../routes/route-manager'

export type PullRequestHeaderProps = HeaderPageData & {
  activeRoute: PullRequestActiveRoute
  stickyHeaderVariant?: StickyPullRequestHeaderProps['pageVariant']
}

/**
 * LivePullRequestHeader is a thin wrapper around PullRequestHeader that will automatically update the header when the
 * alive channel is triggered with `{title_updated: true}`. This lives here instead of the base page so that
 * it doesn't re-render the entire page when the title is updated.
 */
export function LivePullRequestHeader({
  aliveChannels,
  bannersData,
  activeRoute,
  stickyHeaderVariant,
  pullRequest,
  pageTitle,
  repository,
  urls,
  user,
  mergeStatusButtonData,
  stack,
  viewSettings,
}: PullRequestHeaderProps & {aliveChannels: AliveChannels}) {
  const {data: headerData} = useHeaderPageData({
    aliveChannels,
    bannersData,
    pageTitle,
    pullRequest,
    repository,
    stack,
    urls,
    user,
    mergeStatusButtonData,
    viewSettings,
  })
  useHeaderLiveUpdates(aliveChannels)

  return <PullRequestHeader {...headerData} activeRoute={activeRoute} stickyHeaderVariant={stickyHeaderVariant} />
}

export function PullRequestHeader({
  bannersData,
  activeRoute,
  stickyHeaderVariant,
  pullRequest,
  repository,
  pageTitle,
  urls,
  user,
  mergeStatusButtonData,
  stack,
}: PullRequestHeaderProps) {
  const {dockedPanelSupported} = getRouteConfig(activeRoute)
  const [isEditing, setIsEditing] = useState(false)
  const editButtonRedesign = isFeatureEnabled('pull_request_edit_button_redesign')
  const underlineNavEnabled = isFeatureEnabled('pull_request_header_underline_nav')
  const showNavDiffstat = !isFeatureEnabled('pull_request_header_diffstat')

  const shouldShowLoggedInOptions = isLoggedIn()
  const isPROpen = isPullRequestOpenState(pullRequest.state)
  const hasMergeStatusButton = isFeatureEnabled('pull_request_merge_status_button')
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const headerButtonSize = hasMergeStatusButton ? 'medium' : 'small'
  const hasValidatedMergeData = validatedMergeData !== null

  const sidePanelOpen = useIsDockedPanelOpen()
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const robustSmallScreenSupport = useFeatureFlag('prx_robust_small_screen_support')

  // Docked merge status panel — only active on files/changes routes
  const isPrsDockedSidePanelEnabled = isFeatureEnabled('prs_docked_side_panel')
  const isDockedMode = !!(dockedPanelSupported && isPrsDockedSidePanelEnabled)
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)

  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel(DockedPanel.MergeStatus, mergeStatusButtonRef.current)
  }, [dockedPanelActions])

  const isMergeStatusPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)

  // Auto-close the docked merge status panel when the PR is merged or closed.
  // After merge, validatedMergeData becomes null which unmounts MergeStatusButton
  useEffect(() => {
    if (isDockedMode && isMergeStatusPanelOpen && !hasValidatedMergeData) {
      dockedPanelActions.closePanel()
    }
  }, [isDockedMode, isMergeStatusPanelOpen, hasValidatedMergeData, dockedPanelActions])

  useEffect(() => {
    if (pageTitle) {
      setTitle(pageTitle)
    }
  }, [pageTitle])

  const feedbackRenderLocation = useResponsiveValue({narrow: 'mobile', regular: 'desktop'}, 'desktop') as
    | 'mobile'
    | 'desktop'

  const titleActions = () => (
    <>
      <div className="js-pull-header-details" data-pull-is-open={isPROpen ? 'true' : 'false'} hidden />
      {activeRoute === 'files_changed' && (
        <FilesChangedFeedback repository={repository} urls={urls} renderLocation={feedbackRenderLocation} />
      )}
      {shouldShowLoggedInOptions && (
        <div className="d-flex gap-1">
          {user.canEditTitle && !editButtonRedesign && (
            <Button onClick={() => setIsEditing(true)} size={headerButtonSize}>
              Edit
            </Button>
          )}
          {validatedMergeData && (
            <MergeMethodContextProvider defaultMergeMethod={validatedMergeData.defaultMergeMethod}>
              <MergeStatusButtonWrapper
                channels={validatedMergeData.channels}
                headSha={validatedMergeData.headSha}
                helpUrl={validatedMergeData.helpUrl}
                viewerLogin={validatedMergeData.viewerLogin}
                isDockedMode={isDockedMode}
                onToggleDockedPanel={handleToggleDockedMergePanel}
                mergeStatusButtonRef={mergeStatusButtonRef}
              />
              {isDockedMode && (
                <MergeStatusPanel
                  helpUrl={validatedMergeData.helpUrl}
                  viewerLogin={validatedMergeData.viewerLogin}
                  channels={validatedMergeData.channels}
                />
              )}
            </MergeMethodContextProvider>
          )}
          <PullRequestCodeButton
            codespacesEnabled={repository.codespacesEnabled}
            headBranch={pullRequest.headBranch}
            isEnterprise={repository.isEnterprise}
            pullRequestId={pullRequest.id}
            pullRequestNumber={pullRequest.number}
            repository={repository}
            size={headerButtonSize}
          />
        </div>
      )}
    </>
  )

  return (
    <>
      <PullRequestsTopBanners bannersData={bannersData} urls={urls} activeRoute={activeRoute} />
      {isEditing && (
        <PullRequestEditTitleForm
          initialTitle={pullRequest.title}
          pullRequestNumber={pullRequest.number}
          onCloseForm={() => setIsEditing(false)}
        />
      )}
      <PageHeader
        className={clsx(
          'flex-items-center',
          sidePanelOpen && styles.sidePanelOpen,
          styles.actionsAboveTitleOnNarrow,
          robustSmallScreenSupport && styles.actionsAboveTitleOnNarrowContainer,
        )}
      >
        {isEditing && (
          // this is done so that we still have an h1 element present when editing the title
          <h1 className="sr-only">{`${pullRequest.title} - #${pullRequest.number}`}</h1>
        )}
        {!isEditing && (
          <>
            <PageHeader.TitleArea>
              <PageHeader.Title as="h1" className={clsx('lh-condensed', underlineNavEnabled && 'tmp-px-3')}>
                <VerifiedHTMLText className="f1 text-normal markdown-title" html={pullRequest.titleHtml} />
                <span className="d-inline-flex flex-items-center gap-1">
                  <span className="pl-2 fgColor-muted f1-light">#{pullRequest.number}</span>
                  {user.canEditTitle && editButtonRedesign && (
                    <IconButton
                      icon={PencilIcon}
                      onClick={() => setIsEditing(true)}
                      size="medium"
                      aria-label="Edit title"
                      variant="invisible"
                    />
                  )}
                </span>
              </PageHeader.Title>
            </PageHeader.TitleArea>
            <PageHeader.Actions
              className={clsx('flex-items-center gap-2 position-relative', underlineNavEnabled && 'tmp-px-3')}
            >
              {titleActions()}
            </PageHeader.Actions>
          </>
        )}
        <PageHeader.Description
          className={clsx('d-flex flex-column flex-items-start', underlineNavEnabled && 'tmp-px-3')}
        >
          <div className="d-flex flex-column flex-sm-row gap-2 width-full flex-items-start flex-justify-between">
            <div className="d-flex flex-items-center gap-2 flex-shrink-0">
              <PullRequestStateLabel state={pullRequest.state} />
              <StackState pullRequestNumber={pullRequest.number} stack={stack} />
            </div>
            <div className="flex-1 overflow-hidden">
              <PullRequestHeaderSummary
                author={pullRequest.author}
                mergedTime={pullRequest.mergedTime}
                baseBranch={pullRequest.baseBranch}
                baseRepositoryDefaultBranch={repository.defaultBranch}
                baseRepositoryName={repository.name}
                baseRepositoryOwnerLogin={repository.ownerLogin}
                canChangeBase={user.canChangeBase}
                commitsCount={pullRequest.commitsCount}
                headBranch={pullRequest.headBranch}
                headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                headRepositoryName={pullRequest.headRepositoryName}
                isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                isEditing={isEditing}
                mergedBy={pullRequest.mergedBy}
                mergedByAvatarUrl={pullRequest.mergedByAvatarUrl}
                mergedByName={pullRequest.mergedByName}
                repositoryId={repository.id}
                pullRequestId={pullRequest.id}
                pullRequestNumber={pullRequest.number}
                setIsEditing={setIsEditing}
                state={pullRequest.state}
                stack={stack}
              />
            </div>
          </div>
          <PullRequestBanners bannersData={bannersData} pullRequest={pullRequest} repository={repository} />
        </PageHeader.Description>
        <PageHeader.Navigation
          className={clsx(
            underlineNavEnabled ? 'tmp-pt-3 tmp-ml-n3 tmp-mr-n3 tmp-pl-3' : 'tmp-pt-3 tmp-px-3 tmp-ml-n3 tmp-mr-n3',
            underlineNavEnabled && styles.navBorderBottom,
          )}
        >
          <div
            className={clsx(
              styles.diffStatesWrapper,
              underlineNavEnabled ? 'float-right d-none d-md-block tmp-mr-3 tmp-px-3' : 'float-right d-none d-md-block',
            )}
          >
            <ErrorBoundary boundaryName="PullRequestHeaderDiffStat" fallback={null}>
              <Suspense fallback={<LoadingSkeleton width="80px" />}>{showNavDiffstat && <DiffStat />}</Suspense>
            </ErrorBoundary>
          </div>
          <div className="flex-auto">
            <PullRequestHeaderNavigation
              activeRoute={activeRoute}
              pullRequest={pullRequest}
              repository={repository}
              urls={urls}
            />
          </div>
        </PageHeader.Navigation>
      </PageHeader>
      {activeRoute !== 'files_changed' && (
        <StickyPullRequestHeader
          repository={repository}
          pullRequest={pullRequest}
          pageVariant={stickyHeaderVariant ?? 'condensed'}
        />
      )}
    </>
  )
}
